// Phase 22C — Location Map (managers + admin only)
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Users, Package as PackageIcon, Activity, Search, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import PageHeader from "@/components/PageHeader";

// Fix Leaflet default icon URLs under webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LEGEND_COLORS = {
  punch_in:  "#059669",   // emerald
  punch_out: "#dc2626",   // red
  visit:     "#0ea5e9",   // sky — any other visit (in progress / skipped)
  order:     "#16a34a",   // green — productive call (order booked)
  no_order:  "#f97316",   // orange — non-productive call (no order)
  new_call:  "#db2777",   // pink — call on an outlet added that day
  outlet:    "#7c3aed",   // violet
  activity:  "#0d9488",   // teal
  ping:      "#a3a3a3",   // neutral
  gap:       "#f59e0b",   // amber (dashed)
};

// A visit's marker colour: a call on an outlet the rep added that same day is a
// "new call" (whatever its outcome), else order / no order by outcome.
function visitKind(v, newOutletIds) {
  if (v.retailer_id && newOutletIds.has(v.retailer_id)) return "new_call";
  if (v.outcome === "productive") return "order";
  if (v.outcome === "non_productive") return "no_order";
  return "visit";
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    map.fitBounds(points, { padding: [30, 30], maxZoom: 15 });
  }, [points, map]);
  return null;
}

// A Dialog-based picker instead of a Select dropdown — a centered modal
// sized off the real viewport (`max-h-[70vh]`, actual pixels) can't run
// into the "not enough room below the trigger" problem a Popper-anchored
// dropdown has on a short window: content that overflows a viewport-sized
// box is reliably visible+scrollable no matter where the trigger button
// itself sits on the page.
function UserPickerDialog({ open, onOpenChange, users, value, onSelect }) {
  const [q, setQ] = useState("");
  const filtered = users.filter(u => {
    if (!q.trim()) return true;
    const hay = `${u.role_code} ${u.name}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setQ(""); }}>
      <DialogContent className="max-w-md p-0 gap-0" data-testid="user-map-picker-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle>Choose a user</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-2 relative">
          <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name…"
                 className="pl-8" autoFocus data-testid="user-map-picker-search" />
        </div>
        <div className="max-h-[70vh] overflow-y-auto border-t border-neutral-100">
          <button type="button"
                  onClick={() => { onSelect(""); onOpenChange(false); }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-neutral-50 text-neutral-500"
                  data-testid="user-map-picker-clear">
            <span className="w-4" /> Clear selection
          </button>
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-neutral-400">No match.</div>
          ) : filtered.map(u => (
            <button key={u.id} type="button"
                    onClick={() => { onSelect(u.id); onOpenChange(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-neutral-50"
                    data-testid={`user-map-picker-opt-${u.id}`}>
              {value === u.id ? <Check className="h-4 w-4 text-[#6D2C91]" /> : <span className="w-4" />}
              <span className="truncate">{u.role_code} — {u.name}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LocationMap() {
  const [users, setUsers] = useState([]);
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [subZoneId, setSubZoneId] = useState("");
  const [userId, setUserId] = useState("");
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [date, setDate] = useState(new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // page_size=500 — a 200-cap was silently truncating the picker on any
    // org with more than 200 non-distributor users, well below plausible
    // real headcount.
    api.get("/users", { params: { page_size: 500 } })
      .then(r => setUsers((r.data.items || []).filter(u => u.role_code !== "DISTRIBUTOR")))
      .catch(e => toast.error(formatApiError(e)));
    api.get("/zones").then(r => setZones(r.data.items || [])).catch(() => {});
  }, []);

  // Phase 24B — SubZone cascade
  useEffect(() => {
    if (!zoneId) { setSubZones([]); setSubZoneId(""); return; }
    api.get("/geography/sub-zones", { params: { zone_id: zoneId } })
       .then(r => setSubZones(r.data.items || [])).catch(() => setSubZones([]));
  }, [zoneId]);

  // Filter users list by selected zone / subzone (client-side). No longer
  // restricted to users with prior location activity — that hid the vast
  // majority of real users (most simply hadn't been picked yet), and
  // picking a user with no trail is itself a legitimate thing to check
  // ("why does this person have no data") rather than an error state.
  const filteredUsers = users.filter(u => {
    if (zoneId && !(u.assigned_zone_ids || []).includes(zoneId)) return false;
    if (subZoneId && !(u.assigned_sub_zone_ids || []).includes(subZoneId)) return false;
    return true;
  });

  const load = useCallback(async () => {
    if (!userId || !date) return;
    setLoading(true);
    try {
      const r = await api.get("/location/user-map", { params: { user_id: userId, date }});
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); setData(null); }
    finally { setLoading(false); }
  }, [userId, date]);
  useEffect(() => { load(); }, [load]);

  const pings = data?.pings || [];
  const segments = data?.segments || [];
  const visits = data?.visits || [];
  const attendance = data?.attendance || null;
  const stats = data?.header_stats || {};
  const legend = data?.legend_counts || {};

  const line = pings.map(p => [p.lat, p.lng]);
  // Road path (OpenStreetMap routing) — drawn instead of straight "air" lines between pings.
  const roadPath = data?.road_path || [];
  const gapLines = segments.filter(s => s.gap).map(s => {
    const a = pings.find(p => p.id === s.from_id);
    const b = pings.find(p => p.id === s.to_id);
    return a && b ? [[a.lat, a.lng], [b.lat, b.lng]] : null;
  }).filter(Boolean);
  const solidLines = [];
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].gap) continue;
    const a = pings.find(p => p.id === segments[i].from_id);
    const b = pings.find(p => p.id === segments[i].to_id);
    if (a && b) solidLines.push([[a.lat, a.lng], [b.lat, b.lng]]);
  }
  // Fit-bounds needs to cover EVERY point we might render, not just the
  // ping trail — a user with attendance GPS but no location pings (GPS off
  // during the visit, or no trail permission) would otherwise render a
  // correctly-placed punch-in/out marker that's off-screen, with the map
  // stuck at the fallback Ahmedabad coordinate.
  const attendancePoints = [
    attendance?.check_in_gps, attendance?.check_out_gps,
  ].filter(Boolean).map(g => [g.lat, g.lng]);
  const newOutletIds = new Set((data?.outlets_added || []).map(o => o.id));
  const visitKindCounts = visits.reduce((acc, v) => {
    const k = visitKind(v, newOutletIds);
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  // A call punched with a useless GPS reading (e.g. a desktop browser guessing +-50 km) has no real
  // location: it is NOT drawn (that would show a wrong place) and is listed under the map instead.
  const poorVisits = visits.filter(v => v.gps_poor);
  const drawnVisits = visits.filter(v => v.check_in_gps && !v.gps_poor);
  const visitPos = (v) => [v.check_in_gps.lat, v.check_in_gps.lng];
  const visitPoints = drawnVisits.map(visitPos);
  const allPoints = [...line, ...attendancePoints, ...visitPoints];
  const bounds = allPoints.length >= 1 ? allPoints : [[23.0225, 72.5714]];

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="location-map-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Field ops" }, { label: "Location map" }]}
        title="Location map"
        subtitle="Where your team is right now — GPS trail with outlet punches overlaid."
        favorite={{ key: "admin.user-map", type: "screen", label: "Location map", route: "/admin/user-map" }}
        testid="locationmap-page-header"
      />

      <div>
        <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
          <MapPin className="h-6 w-6 text-[#6D2C91]" /> Location Map
        </h1>
        <p className="text-xs text-neutral-500 mt-0.5">
          OpenStreetMap trail with visits, attendance and pings. Visibility respects your zone/hierarchy.
        </p>
      </div>

      {/* Controls — flex-wrap (width-based), not native <select>s of
          inconsistent width crammed into one row. Same pattern as
          CommonFilterBar: shadcn Select for Zone/Sub-Zone/User, each
          taking a share of the actual available width instead of
          shrink-to-content. The User field was a Popover-based
          SearchableSelect, whose selection didn't reliably register —
          likely a stacking-context conflict with the Leaflet map
          underneath — swapped for the plain Select every other report
          page in the app already uses without issue. */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px] text-neutral-500">Zone</Label>
            <Select value={zoneId || "__all__"}
                    onValueChange={v => { const zid = v === "__all__" ? "" : v; setZoneId(zid); setSubZoneId(""); setUserId(""); }}>
              <SelectTrigger className="h-10" data-testid="location-map-zone"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All zones</SelectItem>
                {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.code} — {z.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px] text-neutral-500">Sub-Zone</Label>
            <Select value={subZoneId || "__all__"}
                    onValueChange={v => { setSubZoneId(v === "__all__" ? "" : v); setUserId(""); }}
                    disabled={!zoneId}>
              <SelectTrigger className="h-10" data-testid="location-map-subzone">
                <SelectValue placeholder={zoneId ? "All sub-zones" : "Pick zone first"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All sub-zones</SelectItem>
                {subZones.map(sz => <SelectItem key={sz.id} value={sz.id}>{sz.code} — {sz.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[47%] min-w-[180px] flex-1">
            <Label className="text-[11px] text-neutral-500">User</Label>
            <Button type="button" variant="outline"
                    className="h-10 w-full justify-start font-normal"
                    onClick={() => setUserPickerOpen(true)}
                    data-testid="user-map-user">
              <span className="truncate">
                {filteredUsers.find(u => u.id === userId)
                  ? `${filteredUsers.find(u => u.id === userId).role_code} — ${filteredUsers.find(u => u.id === userId).name}`
                  : <span className="text-neutral-500">Choose a user…</span>}
              </span>
            </Button>
            <UserPickerDialog open={userPickerOpen} onOpenChange={setUserPickerOpen}
                              users={filteredUsers} value={userId} onSelect={setUserId} />
          </div>
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px] text-neutral-500">Date</Label>
            <Input type="date" className="w-full" value={date} onChange={e => setDate(e.target.value)}
                   data-testid="user-map-date" />
          </div>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
        </CardContent>
      </Card>

      {/* Header stats */}
      <div className="flex flex-wrap gap-3" data-testid="user-map-stats">
        <div className="min-w-[130px] flex-1"><StatTile icon={<MapPin className="h-4 w-4" />} label="Distance (by road)" value={`${stats.total_km ?? 0} km`} /></div>
        <div className="min-w-[130px] flex-1"><StatTile icon={<Users className="h-4 w-4" />} label="Visits" value={stats.total_visits ?? 0} /></div>
        <div className="min-w-[130px] flex-1"><StatTile icon={<PackageIcon className="h-4 w-4" />} label="Unique outlets" value={stats.unique_outlets ?? 0} /></div>
        <div className="min-w-[130px] flex-1"><StatTile icon={<Activity className="h-4 w-4" />} label="Geo-tagged" value={stats.geo_tagged_visits ?? 0} /></div>
      </div>

      {/* Map + legend always stack (not grid-cols-1 lg:grid-cols-4 — same
          viewport-vs-actual-width issue as the filters above: this page
          is opened inside a ~480px column via ASM's field shell just as
          often as a full desktop admin window, and `lg:` can't tell the
          difference). Legend below a fixed-height map reads fine at any
          width, so there's no real loss going full-stack here. */}
      <div className="flex flex-col gap-3">
        {poorVisits.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="user-map-poor-gps">
            <b>{poorVisits.length} call{poorVisits.length === 1 ? "" : "s"} with no reliable location (not drawn on the map):</b>
            {poorVisits.map((v) => (
              <div key={v.id}>
                • {v.retailer_name || "Outlet"} — {v.check_in_ts ? new Date(v.check_in_ts).toLocaleTimeString() : ""}
                {v.device ? ` — from ${v.device}` : ""}. The device could only guess its position (±{Math.round((v.gps_accuracy_m || 0) / 1000)} km).
              </div>
            ))}
          </div>
        )}
        {/* Map */}
        <Card className="border-neutral-200 rounded-2xl overflow-hidden">
          <CardContent className="p-0" style={{height: "540px"}} data-testid="user-map-canvas">
            <MapContainer center={[23.0225, 72.5714]} zoom={13}
                            style={{height: "100%", width: "100%"}}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              {roadPath.length > 1 && (
                <Polyline positions={roadPath} pathOptions={{ color: "#6D2C91", weight: 5, opacity: 0.85 }} />
              )}
              {roadPath.length <= 1 && solidLines.map((seg, i) => (
                <Polyline key={"s"+i} positions={seg}
                            pathOptions={{ color: "#6D2C91", weight: 4, opacity: 0.75 }} />
              ))}
              {roadPath.length <= 1 && gapLines.map((seg, i) => (
                <Polyline key={"g"+i} positions={seg}
                            pathOptions={{ color: LEGEND_COLORS.gap, weight: 3, opacity: 0.7,
                                           dashArray: "10,8" }} />
              ))}
              {pings.map(p => (
                <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={3}
                                 pathOptions={{ color: LEGEND_COLORS.ping, fillColor: LEGEND_COLORS.ping, fillOpacity: 0.6 }} />
              ))}
              {attendance?.check_in_gps && (
                <CircleMarker center={[attendance.check_in_gps.lat, attendance.check_in_gps.lng]}
                                 radius={10}
                                 pathOptions={{ color: LEGEND_COLORS.punch_in, fillColor: LEGEND_COLORS.punch_in, fillOpacity: 0.7 }}>
                  <Popup>Punch-in · {new Date(attendance.check_in_ts).toLocaleTimeString()}</Popup>
                </CircleMarker>
              )}
              {attendance?.check_out_gps && (
                <CircleMarker center={[attendance.check_out_gps.lat, attendance.check_out_gps.lng]}
                                 radius={10}
                                 pathOptions={{ color: LEGEND_COLORS.punch_out, fillColor: LEGEND_COLORS.punch_out, fillOpacity: 0.7 }}>
                  <Popup>Punch-out · {new Date(attendance.check_out_ts).toLocaleTimeString()}</Popup>
                </CircleMarker>
              )}
              {drawnVisits.map(v => {
                const kind = visitKind(v, newOutletIds);
                const kindLabel = { new_call: "New call", order: "Order", no_order: "No order", visit: "Visit" }[kind];
                const c = LEGEND_COLORS[kind];
                return (
                <CircleMarker key={v.id} center={visitPos(v)}
                                 radius={7}
                                 pathOptions={{ color: c, fillColor: c, fillOpacity: 0.75 }}>
                  <Popup>
                    <div className="text-xs space-y-0.5 min-w-[190px]" data-testid="user-map-visit-popup">
                      <div className="font-semibold text-sm" style={{ color: c }}>{kindLabel}</div>
                      <div className="font-semibold">{v.retailer_name || v.retailer_id?.slice(0,6)}</div>
                      {v.retailer_code && <div>Code: {v.retailer_code}</div>}
                      {v.owner_name && <div>Owner: {v.owner_name}</div>}
                      {v.phone && <div>Phone: <a href={`tel:${v.phone}`}>{v.phone}</a></div>}
                      {v.address && <div>Address: {v.address}</div>}
                      {v.channel_type && <div>Channel: {v.channel_type}</div>}
                      <div className="pt-1 border-t mt-1">
                        {v.check_in_ts && <>In: {new Date(v.check_in_ts).toLocaleTimeString()}</>}
                        {v.duration_sec ? <> · {Math.max(1, Math.round(v.duration_sec / 60))} min</> : null}
                      </div>
                      {v.device && <div>Punched from: <b>{v.device}</b>{v.device_ip ? ` · ${v.device_ip}` : ""}</div>}

                      {v.order_count > 0 && (
                        <div>Order: {v.order_kg} kg · ₹{Number(v.order_value).toLocaleString("en-IN")}
                          {v.order_count > 1 ? ` (${v.order_count} orders)` : ""}</div>
                      )}
                      {v.outcome === "non_productive" && (
                        <div>No order{v.non_productive_reason ? ` — ${String(v.non_productive_reason).replace(/_/g, " ")}` : ""}</div>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
                );
              })}
              {allPoints.length > 0 && <FitBounds points={bounds} />}
            </MapContainer>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-3 space-y-1.5 text-sm" data-testid="user-map-legend">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Legend</div>
            {[
              [roadPath.length > 1 ? "Road route" : "Trail solid", "#6D2C91", legend.pings || 0, "pings"],
              ["Gap (dashed)", LEGEND_COLORS.gap, legend.gap_segments || 0, "segments"],
              ["Punch-in", LEGEND_COLORS.punch_in, legend.punch_in || 0, ""],
              ["Punch-out", LEGEND_COLORS.punch_out, legend.punch_out || 0, ""],
              ["Order", LEGEND_COLORS.order, visitKindCounts.order || 0, "calls"],
              ["No order", LEGEND_COLORS.no_order, visitKindCounts.no_order || 0, "calls"],
              ["New call", LEGEND_COLORS.new_call, visitKindCounts.new_call || 0, "calls"],
              ...((visitKindCounts.visit || 0) > 0
                ? [["Other visits", LEGEND_COLORS.visit, visitKindCounts.visit, ""]] : []),
              ["Activities", LEGEND_COLORS.activity, legend.activities || 0, ""],
            ].map(([label, color, count, suffix]) => (
              <div key={label} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{background: color}} /> {label}
                </span>
                <span className="text-neutral-500">{count} {suffix}</span>
              </div>
            ))}
            {!data && userId && !loading && (
              <div className="text-[11px] text-neutral-400 italic mt-2">Select a date with pings.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatTile({ icon, label, value }) {
  return (
    <Card className="border-neutral-200 rounded-2xl">
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500">{icon}{label}</div>
        <div className="text-2xl font-bold text-neutral-900 mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}
